import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { API } from "@/utils/constants";

export const TeamStatsDialog = ({ open, onClose, authPassword }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [tab, setTab] = useState("team");
  const [stats, setStats] = useState(null);
  const [venues, setVenues] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const headers = { "X-Auth-Password": authPassword };
    const req = tab === "team"
      ? axios.get(`${API}/team-stats?year=${year}`, { headers }).then(r => setStats(r.data))
      : axios.get(`${API}/venue-stats?year=${year}`, { headers }).then(r => setVenues(r.data));
    req.catch(e => console.error("Failed to fetch stats:", e)).finally(() => setLoading(false));
  }, [open, year, tab, authPassword]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="team-stats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-6">
            <span>Stats — Weddings</span>
            <span className="flex items-center gap-1">
              <button type="button" onClick={() => setYear(year - 1)} className="h-7 w-7 rounded hover:bg-gray-100 text-gray-500" data-testid="team-stats-prev-year">‹</button>
              <span className="text-sm font-mono w-12 text-center">{year}</span>
              <button type="button" onClick={() => setYear(year + 1)} className="h-7 w-7 rounded hover:bg-gray-100 text-gray-500" data-testid="team-stats-next-year">›</button>
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 border-b border-gray-100 -mt-1">
          <button
            type="button"
            onClick={() => setTab("team")}
            className={`px-3 py-1.5 text-sm font-medium rounded-t ${tab === "team" ? "text-[#2d2d2d] border-b-2 border-[#605e5e]" : "text-gray-400 hover:text-gray-600"}`}
            data-testid="stats-tab-team"
          >
            Team
          </button>
          <button
            type="button"
            onClick={() => setTab("venues")}
            className={`px-3 py-1.5 text-sm font-medium rounded-t ${tab === "venues" ? "text-[#2d2d2d] border-b-2 border-[#605e5e]" : "text-gray-400 hover:text-gray-600"}`}
            data-testid="stats-tab-venues"
          >
            Venues
          </button>
        </div>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#605e5e]" /></div>
        ) : tab === "team" && stats ? (
          <div className="space-y-3">
            <div className="flex gap-4 text-sm text-[#605e5e]" data-testid="team-stats-totals">
              <span><strong>{stats.total_weddings}</strong> weddings</span>
              <span className="text-gray-400">|</span>
              <span><strong>{stats.total_done}</strong> done</span>
              <span><strong>{stats.total_left}</strong> to come</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] text-gray-400 uppercase tracking-wide border-b border-gray-100">
                    <th className="text-left py-1.5 px-1">Member</th>
                    <th className="text-center py-1.5 px-1">Done</th>
                    <th className="text-center py-1.5 px-1">To Come</th>
                    <th className="text-center py-1.5 px-1 font-semibold">Total</th>
                    <th className="text-center py-1.5 px-1">Photo</th>
                    <th className="text-center py-1.5 px-1">Video</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.members.map(m => (
                    <tr key={m.name} className="odd:bg-gray-50" data-testid={`team-stats-row-${m.name}`}>
                      <td className="py-1.5 px-1 font-medium text-[#2d2d2d]">{m.name}</td>
                      <td className="py-1.5 px-1 text-center text-gray-600">{m.done}</td>
                      <td className="py-1.5 px-1 text-center text-gray-600">{m.left}</td>
                      <td className="py-1.5 px-1 text-center font-semibold text-[#2d2d2d]">{m.total}</td>
                      <td className="py-1.5 px-1 text-center text-gray-600">{m.photo}{m.mixed > 0 && <span className="text-amber-500">*</span>}</td>
                      <td className="py-1.5 px-1 text-center text-gray-600">{m.video}</td>
                    </tr>
                  ))}
                  {stats.members.length === 0 && (
                    <tr><td colSpan={6} className="py-6 text-center text-gray-400">No weddings found for {year}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-gray-400">
              Photo/Video split: on photo &amp; video packages the photographer is Andy if assigned, otherwise Ellis.
              {stats.members.some(m => m.mixed > 0) && <> * includes jobs where the photographer couldn't be determined.</>}
            </p>
          </div>
        ) : tab === "venues" && venues ? (
          <div className="space-y-3">
            <div className="text-sm text-[#605e5e]" data-testid="venue-stats-totals">
              <strong>{venues.venues.length}</strong> venues in {venues.year}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] text-gray-400 uppercase tracking-wide border-b border-gray-100">
                    <th className="text-left py-1.5 px-1">Venue</th>
                    <th className="text-center py-1.5 px-1">Done</th>
                    <th className="text-center py-1.5 px-1">To Come</th>
                    <th className="text-center py-1.5 px-1 font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {venues.venues.map(v => (
                    <tr key={v.venue} className="odd:bg-gray-50" data-testid={`venue-stats-row-${v.venue}`}>
                      <td className="py-1.5 px-1 font-medium text-[#2d2d2d]">{v.venue}</td>
                      <td className="py-1.5 px-1 text-center text-gray-600">{v.done}</td>
                      <td className="py-1.5 px-1 text-center text-gray-600">{v.left}</td>
                      <td className="py-1.5 px-1 text-center font-semibold text-[#2d2d2d]">{v.total}</td>
                    </tr>
                  ))}
                  {venues.venues.length === 0 && (
                    <tr><td colSpan={4} className="py-6 text-center text-gray-400">No weddings found for {year}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
